import { useState, useCallback, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "./ui/skeleton";
import { Dialog, DialogContent } from "./ui/dialog";
import { X, ChevronLeft, ChevronRight, ZoomIn } from "lucide-react";
import { Button } from "./ui/button";
import { useIsMobile } from "@/hooks/use-mobile";
import { motion } from "framer-motion";
import useEmblaCarousel from "embla-carousel-react";

interface GalleryImage {
  id: string;
  image_url: string;
  alt_text: string;
  caption: string | null;
  display_order: number;
}

// Dot indicators for mobile carousel
const DotIndicators = ({
  count,
  activeIndex,
  onDotClick,
}: {
  count: number;
  activeIndex: number;
  onDotClick: (index: number) => void;
}) => (
  <div className="flex justify-center gap-2 mt-6" role="tablist" aria-label="Gallery navigation">
    {[...Array(count)].map((_, i) => (
      <button
        key={i}
        onClick={() => onDotClick(i)}
        className={`h-2.5 rounded-full transition-all duration-300 touch-target ${
          i === activeIndex
            ? "bg-azure-500 w-6"
            : "bg-azure-500/30 hover:bg-azure-500/50 w-2.5"
        }`}
        role="tab"
        aria-selected={i === activeIndex}
        aria-label={`Go to image ${i + 1}`}
      />
    ))}
  </div>
);

// Image card with hover effects
const GalleryImageCard = ({
  image,
  index,
  onClick,
  isMobile,
}: {
  image: GalleryImage;
  index: number;
  onClick: () => void;
  isMobile: boolean;
}) => {
  return (
    <motion.button
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: index * 0.08 }}
      viewport={{ once: true, margin: "-50px" }}
      onClick={onClick}
      className="relative overflow-hidden rounded-xl group cursor-pointer focus:outline-none focus:ring-2 focus:ring-azure-500 focus:ring-offset-2 aspect-[4/3] w-full"
      aria-label={`View ${image.alt_text} in fullscreen`}
    >
      {/* Image with lazy loading and blur-up effect */}
      <img
        src={image.image_url}
        alt={image.alt_text}
        width={600}
        height={450}
        sizes="(max-width: 768px) 85vw, (max-width: 1024px) 33vw, 25vw"
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        loading="lazy"
        decoding="async"
      />
      
      {/* Hover overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-navy-900/80 via-navy-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-300 flex flex-col items-center justify-end p-4">
        <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 flex flex-col items-center gap-2">
          <div className="bg-white/20 backdrop-blur-sm rounded-full p-3">
            <ZoomIn className="h-5 w-5 text-white" />
          </div>
          {image.caption && (
            <span className="text-white text-sm font-medium text-center line-clamp-2 max-w-[200px]">
              {image.caption}
            </span>
          )}
        </div>
      </div>
    </motion.button>
  );
};

// Premium lightbox component
const Lightbox = ({
  images,
  currentIndex,
  isOpen,
  onClose,
  onPrevious,
  onNext,
  onDotClick,
}: {
  images: GalleryImage[];
  currentIndex: number;
  isOpen: boolean;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
  onDotClick: (index: number) => void;
}) => {
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case "ArrowLeft":
          onPrevious();
          break;
        case "ArrowRight":
          onNext();
          break;
        case "Escape":
          onClose();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onPrevious, onNext, onClose]);

  // Touch swipe handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    if (!touchStartX.current || !touchEndX.current) return;

    const swipeDistance = touchStartX.current - touchEndX.current;
    const minSwipeDistance = 50;

    if (Math.abs(swipeDistance) > minSwipeDistance) {
      if (swipeDistance > 0) {
        onNext();
      } else {
        onPrevious();
      }
    }

    touchStartX.current = 0;
    touchEndX.current = 0;
  };

  const currentImage = images[currentIndex];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] md:max-w-7xl w-full h-[90vh] p-0 bg-navy-900/95 backdrop-blur-xl border-azure-500/20">
        <div
          className="relative w-full h-full flex flex-col"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >
          {/* Header with counter and close button */}
          <div className="absolute top-0 left-0 right-0 z-20 flex items-center justify-between p-4 bg-gradient-to-b from-navy-900/80 to-transparent">
            <span className="text-white/80 text-sm font-medium bg-navy-900/50 backdrop-blur-sm px-3 py-1.5 rounded-full">
              {currentIndex + 1} / {images.length}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="text-white hover:bg-white/20 rounded-full"
              onClick={onClose}
              aria-label="Close lightbox"
            >
              <X className="h-6 w-6" />
            </Button>
          </div>

          {/* Main image area */}
          <div className="flex-1 flex items-center justify-center px-4 py-16">
            {currentImage && (
              <motion.img
                key={currentImage.id}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
                src={currentImage.image_url}
                alt={currentImage.alt_text}
                className="max-h-full max-w-full object-contain rounded-lg shadow-2xl"
              />
            )}
          </div>

          {/* Navigation arrows */}
          {images.length > 1 && (
            <>
              <Button
                variant="ghost"
                size="icon"
                className="absolute left-2 md:left-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 rounded-full h-12 w-12 md:h-14 md:w-14"
                onClick={onPrevious}
                aria-label="Previous image"
              >
                <ChevronLeft className="h-8 w-8" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="absolute right-2 md:right-4 top-1/2 -translate-y-1/2 text-white hover:bg-white/20 rounded-full h-12 w-12 md:h-14 md:w-14"
                onClick={onNext}
                aria-label="Next image"
              >
                <ChevronRight className="h-8 w-8" />
              </Button>
            </>
          )}

          {/* Caption and dot indicators */}
          <div className="absolute bottom-0 left-0 right-0 z-20 bg-gradient-to-t from-navy-900/90 to-transparent p-4 pt-8">
            {currentImage?.caption && (
              <p className="text-white text-center text-sm md:text-base mb-4 max-w-2xl mx-auto">
                {currentImage.caption}
              </p>
            )}
            {images.length > 1 && images.length <= 12 && (
              <div className="flex justify-center gap-1.5">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => onDotClick(i)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      i === currentIndex
                        ? "bg-azure-500 w-6"
                        : "bg-white/40 hover:bg-white/60 w-2"
                    }`}
                    aria-label={`Go to image ${i + 1}`}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const Gallery = () => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const isMobile = useIsMobile();

  // Mobile carousel
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "center",
    loop: true,
    slidesToScroll: 1,
  });

  const { data: images, isLoading } = useQuery({
    queryKey: ["gallery"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("gallery_images")
        .select("*")
        .eq("publish", true)
        .order("display_order", { ascending: true });

      if (error) throw error;
      return data as GalleryImage[];
    },
  });

  // Embla carousel callbacks
  const scrollTo = useCallback((index: number) => emblaApi?.scrollTo(index), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;

    const onSelect = () => {
      setSelectedIndex(emblaApi.selectedScrollSnap());
    };

    emblaApi.on("select", onSelect);
    onSelect();

    return () => {
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  // Lightbox handlers
  const openLightbox = (index: number) => {
    setCurrentIndex(index);
    setLightboxOpen(true);
  };

  const goToPrevious = useCallback(() => {
    if (images) {
      setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    }
  }, [images]);

  const goToNext = useCallback(() => {
    if (images) {
      setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    }
  }, [images]);

  const goToImage = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <section id="gallery" className="py-16 md:py-24 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Skeleton className="h-10 w-64 mx-auto mb-4" />
            <Skeleton className="h-6 w-80 mx-auto" />
          </div>
          {isMobile ? (
            <Skeleton className="aspect-[4/3] w-full rounded-xl" />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {[...Array(8)].map((_, i) => (
                <Skeleton key={i} className="rounded-xl aspect-[4/3]" />
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }

  // Empty state - return null to hide section
  if (!images || images.length === 0) {
    return null;
  }

  return (
    <>
      <section id="gallery" className="py-16 md:py-24 bg-background overflow-hidden">
        <div className="container mx-auto px-4">
          {/* Section Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-foreground">
              Projects Gallery
            </h2>
            
            <p className="hidden md:block text-lg text-muted-foreground max-w-2xl mx-auto">
              Real before, during, and after images from our recent installs and repairs.
            </p>
          </motion.div>

          {/* Mobile: Horizontal Carousel */}
          {isMobile ? (
            <div className="relative">
              <div className="overflow-hidden" ref={emblaRef}>
                <div className="flex gap-4">
                  {images.map((image, index) => (
                    <div key={image.id} className="flex-none w-[85%] min-w-0">
                      <GalleryImageCard
                        image={image}
                        index={index}
                        onClick={() => openLightbox(index)}
                        isMobile={true}
                      />
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Dot indicators for mobile */}
              {images.length > 1 && (
                <DotIndicators
                  count={Math.min(images.length, 10)}
                  activeIndex={selectedIndex % 10}
                  onDotClick={scrollTo}
                />
              )}
            </div>
          ) : (
            /* Desktop: Masonry-inspired Grid */
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 max-w-7xl mx-auto">
              {images.map((image, index) => (
                <GalleryImageCard
                  key={image.id}
                  image={image}
                  index={index}
                  onClick={() => openLightbox(index)}
                  isMobile={false}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Premium Lightbox */}
      <Lightbox
        images={images}
        currentIndex={currentIndex}
        isOpen={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        onPrevious={goToPrevious}
        onNext={goToNext}
        onDotClick={goToImage}
      />
    </>
  );
};